import * as React from 'react';
import { NonRouterModal } from './Modal';
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ExpandControlledAccordion, AccordionDetails, SwitchedAccordionSummary } from '../subs/StyledAccordions';
import { BlueSwitch } from '../subs/StyledSwitches';
import { AlertProps } from '@mui/material/Alert';
import { Workshop, getActiveUserWorkshops } from '../types/Workshop';
import { copyArrayWithIndexChanged } from '../Utils';

import ImportExportIcon from '@mui/icons-material/ImportExport';
import { useSnackbar } from '../../contexts/SnackBarContext';
import { getWorkshopProductsWithInterface, importWorkshopProductSettings, WorkshopProductImportSelection, WorkshopProductWIthInterface } from '../types/WorkshopProducts';
import { TestCombinationInterfaces, gettestcombinationinterfaceslist } from '../types/TestCombinations';
import { WorkshopProductCombination } from '../types/WorkshopProductCombination';
import { WorkshopProductInterface } from '../types/WorkshopProductInterface';
import { getWorkshopInterfaces } from '../types/Workshop';
import { WorkshopInterface } from '../types/WorkshopInterface';
import { Interface } from '../types/Interface';

interface IdSelector {
    sId: number,
    sSelected: boolean
};

interface SelectedWorkshopInteractions {
    productId: IdSelector,
    testCombinationConsumerIds: IdSelector[],
    testCombinationProviderIds: IdSelector[],
    interfaceIds: IdSelector[],
    disabledTestCombinationIds: number[],
    disabledInterfaceIds: number[]
};

const GetNewIdSelector = () => {
    let ids: IdSelector = {
        sId: 0,
        sSelected: false
    }
    return ids;
}

const GetNewSelectedWorkshopInteractions = () => {
    let wsptc: SelectedWorkshopInteractions = {
        productId: GetNewIdSelector(),
        testCombinationConsumerIds: [],
        testCombinationProviderIds: [],
        interfaceIds: [],
        disabledTestCombinationIds: [],
        disabledInterfaceIds: []
    }
    return wsptc;
}

export const ImportMultiProductModal = ({ workshopId, openState, setOpenState }:
    {
        workshopId: number, 
        openState: boolean,
        setOpenState: React.Dispatch<React.SetStateAction<boolean>>
    }) => {
    const { showSnackbar } = useSnackbar();
    const [selWorkshop, setSelWorkshop] = React.useState<Workshop | null>(null);
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [destWorkshopInterfaces, setDestWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [sourceWorkshopInterfaces, setSourceWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [workshopProds, setWorkshopProds] = React.useState<WorkshopProductWIthInterface[]>([]);
    const [selectionInfos, setSelectionInfos] = React.useState<SelectedWorkshopInteractions[]>([]);
    const [supportedCombos, setSupportedCombos] = React.useState<TestCombinationInterfaces[]>([]);
    const [expanded, setExpanded] = React.useState<string>('');

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetch = async () => {
            if (openState === false) return;

            setSelWorkshop(null);
            setWorkshopProds([]);
            setDestWorkshopInterfaces([]);
            setSourceWorkshopInterfaces([]);
            setSupportedCombos([]);
            setSelectionInfos([]);

            try {
                getActiveUserWorkshops(new AbortController()).then((workshopsResponse) => {
                    if (workshopsResponse.failed) {
                        workshopsResponse.errorText && showUserFeedback(workshopsResponse.errorText);
                        return;
                    }
                    const filteredWorkshops = workshopsResponse.result.filter(w => w.id !== workshopId);
                    setWorkshops(filteredWorkshops);
                }).catch((error) => {
                    showUserFeedback(String(error));
                });

                gettestcombinationinterfaceslist(new AbortController()).then((testComboSupportResponse) => {
                    if (testComboSupportResponse.failed) {
                        testComboSupportResponse.errorText && showUserFeedback(testComboSupportResponse.errorText);
                        return;
                    }
                    setSupportedCombos(testComboSupportResponse.result);
                }).catch((error) => {
                    showUserFeedback(String(error));
                });

                getWorkshopInterfaces(workshopId, new AbortController()).then((workshopifacesResponse) => {
                    if (workshopifacesResponse.failed) {
                        workshopifacesResponse.errorText && showUserFeedback(workshopifacesResponse.errorText);
                        return;
                    }
                    setDestWorkshopInterfaces(workshopifacesResponse.result);
                }).catch((error) => {
                    showUserFeedback(String(error));
                });

            } catch (error) {
                showUserFeedback(String(error));
            }
        }

        if (openState === true) fetch();
    }, [workshopId, openState, showUserFeedback]);

    React.useEffect(() => {
        const fetch = async () => {
            if (!selWorkshop) return;
            try {
                getWorkshopProductsWithInterface(selWorkshop.id, new AbortController()).then((productsResponse) => {
                    if (productsResponse.failed) {
                        productsResponse.errorText && showUserFeedback(productsResponse.errorText);
                        return;
                    }
                    setWorkshopProds(productsResponse.result);
                }).catch((error) => {
                    showUserFeedback(String(error));
                });

                getWorkshopInterfaces(selWorkshop.id, new AbortController()).then((workshopifacesResponse) => {
                    if (workshopifacesResponse.failed) {
                        workshopifacesResponse.errorText && showUserFeedback(workshopifacesResponse.errorText);
                        return;
                    }
                    setSourceWorkshopInterfaces(workshopifacesResponse.result);
                }).catch((error) => {
                    showUserFeedback(String(error));
                });

            } catch (error) {
                showUserFeedback(String(error));
            }
        }

        fetch();
    }, [selWorkshop, showUserFeedback]);

    React.useEffect(() => {
        let selectionInfosLoc: SelectedWorkshopInteractions[] = [];
        let selectionInfo: SelectedWorkshopInteractions;
        let someSupported: boolean = false;
        let someAvailable: boolean = false;
        let destSupported: boolean = false;
        let sourceSupported: boolean = false;

        // check what is supported by both workshops, for each product, and set selectionInfo accordingly
        workshopProds.forEach(wp => {
            selectionInfo = GetNewSelectedWorkshopInteractions();
            selectionInfo.productId.sId = wp.wsProduct.id;
            selectionInfo.productId.sSelected = true;

            supportedCombos.forEach(comb => {
                someAvailable = someSupported = false;

                comb.interfaces.forEach(iface => {
                    const wpIface = wp.interfaces.find(wpIface => wpIface.interfaceId === iface.id);
                    destSupported = destWorkshopInterfaces.some(diface => diface.interfaceId === iface.id);
                    sourceSupported = sourceWorkshopInterfaces.some(siface => siface.interfaceId === iface.id);

                    if (!destSupported && !sourceSupported) return null; // neither workshop supports this interface, ignore it
                    someSupported = true;       // something is this combination is supported by at least one workshop, so we will add it to the selectionInfo
                    if (wpIface && wpIface.supportedByProduct && destSupported && sourceSupported) { // both workshops support this interface, add to selectionInfo
                        selectionInfo.interfaceIds.push({ sId: iface.id, sSelected: true });
                        someAvailable = true;
                    }
                    else { // only supported by one workshop, add to disabled list
                        selectionInfo.disabledInterfaceIds.push(iface.id);
                    }
                });

                    // if someAvailable, add to selectionInfo.testCombinationConsumerIds
                if (someAvailable) {
                    const wptc = wp.combinations.find(wptc => wptc.testCombinationId === comb.testCombination.id);

                    selectionInfo.testCombinationConsumerIds.push({ sId: comb.testCombination.id ?? 0, sSelected: wptc?.isConsumer ?? false });
                    selectionInfo.testCombinationProviderIds.push({ sId: comb.testCombination.id ?? 0, sSelected: wptc?.isProvider ?? false });
                }
                    // else if someSupported add to selectionInfo.disabledTestCombinationIds
                else if (someSupported) selectionInfo.disabledTestCombinationIds.push(comb.testCombination.id ?? 0);
            });

            if (selectionInfo.testCombinationConsumerIds.length > 0 && selectionInfo.interfaceIds.length > 0) {
                selectionInfosLoc.push(selectionInfo);
            }
        });

        setSelectionInfos(selectionInfosLoc);

    }, [workshopProds, supportedCombos, destWorkshopInterfaces, sourceWorkshopInterfaces]);

    const handleAccordionChange = (panel: string) =>
        (event: React.SyntheticEvent, newExpanded: boolean) =>
        {
            if (newExpanded) setExpanded(panel);
                else setExpanded('');
        };

    const handleSelectionChange = (productId: number, consumerId: number, providerId: number, iface: number, selected: boolean) => {
        let selectionInfosLoc = [...selectionInfos];

        let selectionInfo = selectionInfosLoc.find(si => si.productId.sId === productId);
        if (selectionInfo) {
            // if consumerID & providerID & iface are all 0, then the product selection is being changed
            if (consumerId === 0 && providerId === 0 && iface === 0) {
                selectionInfo.productId.sSelected = selected;
            }
                // if iface is not 0, then the interface selection is being changed
            else if (iface !== 0) {
                let ifaceSel = selectionInfo.interfaceIds.find(i => i.sId === iface);
                if (ifaceSel) {
                    ifaceSel.sSelected = selected;
                    // replace interface in selectionInfo.interfaceIds with updated interface
                    selectionInfo.interfaceIds = copyArrayWithIndexChanged(selectionInfo.interfaceIds,
                        selectionInfo.interfaceIds.findIndex(i => i.sId === iface), ifaceSel);
                }
            }
            // if consumerID is not 0, then the consumer selection is being changed
            else if (consumerId !== 0) {
                let consumer = selectionInfo.testCombinationConsumerIds.find(tc => tc.sId === consumerId);
                if (consumer) {
                    consumer.sSelected = selected;
                    // replace consumer in selectionInfo.testCombinationConsumerIds with updated consumer
                    selectionInfo.testCombinationConsumerIds = copyArrayWithIndexChanged(selectionInfo.testCombinationConsumerIds,
                        selectionInfo.testCombinationConsumerIds.findIndex(tc => tc.sId === consumerId), consumer);
                }
            }
            // if providerID is not 0, then the provider selection is being changed
            else if (providerId !== 0) {
                let provider = selectionInfo.testCombinationProviderIds.find(tc => tc.sId === providerId);
                if (provider) {
                    provider.sSelected = selected;
                    // replace provider in selectionInfo.testCombinationProviderIds with updated provider
                    selectionInfo.testCombinationProviderIds = copyArrayWithIndexChanged(selectionInfo.testCombinationProviderIds,
                        selectionInfo.testCombinationProviderIds.findIndex(tc => tc.sId === providerId), provider);
                }
            }
            // replace selectionInfo in selectionInfosLoc with updated selectionInfo
            selectionInfosLoc = copyArrayWithIndexChanged(selectionInfosLoc,
                selectionInfosLoc.findIndex(si => si.productId.sId === productId), selectionInfo);
            setSelectionInfos(selectionInfosLoc);
        }
    };

    const importProductsToWorkshop = async () => {
        let workshopProductImportSelections: WorkshopProductImportSelection[] = [];
        let workshopProductCombinations: WorkshopProductCombination[] = [];
        let workshopProductInterfaces: WorkshopProductInterface[] = [];

        // build the workshopProductImportSelections array from the selectionInfos array, which contains the selected products, test combinations, and interfaces
        selectionInfos.forEach(si => {
            workshopProductCombinations = [];       // reset the workshopProductCombinations array for each product
            workshopProductInterfaces = [];         // reset the workshopProductInterfaces array for each product

            if (si.productId.sSelected) {           // if the product is not selected, don't import it
                si.testCombinationConsumerIds.forEach(tc => {
                    if (tc.sSelected) {             // if the test combination is selected, add it to the workshopProductCombinations array
                        workshopProductCombinations.push({
                            workshopProductId: si.productId.sId,
                            testCombinationId: tc.sId,
                            isConsumer: true,
                            isProvider: false,
                            providerReadyToTest: false,
                            consumerReadyToTest: false
                        });
                    }
                });
                si.testCombinationProviderIds.forEach(tc => {
                    if (tc.sSelected) {             // if the test combination is selected, add it to the workshopProductCombinations array
                        let existingCombination = workshopProductCombinations.find(wpc => wpc.workshopProductId === si.productId.sId && wpc.testCombinationId === tc.sId);
                        if (!existingCombination) { // it may already be in the array if it was added as a consumer also
                            workshopProductCombinations.push({
                                workshopProductId: si.productId.sId,
                                testCombinationId: tc.sId,
                                isConsumer: false,
                                isProvider: true,
                                providerReadyToTest: false,
                                consumerReadyToTest: false
                            });
                        }
                        else {
                            existingCombination.isProvider = true;
                            workshopProductCombinations = copyArrayWithIndexChanged(workshopProductCombinations,
                                workshopProductCombinations.findIndex(wpc => wpc.workshopProductId === si.productId.sId && wpc.testCombinationId === tc.sId),
                                existingCombination
                            );
                        }
                    }
                });

                si.interfaceIds.forEach(iface => {
                    if (iface.sSelected) {
                        if (workshopProductInterfaces.find(wpi => wpi.interfaceId === iface.sId && wpi.workshopProductId === si.productId.sId)) return; // already added, skip it (can be in multiple combinations)
                        const serverEndpoint = workshopProds.find(wp => wp.wsProduct.id === si.productId.sId)?.interfaces.find(wpIface => wpIface.interfaceId === iface.sId)?.serverEndpoint;
                        workshopProductInterfaces.push({
                            serverEndpoint: serverEndpoint,
                            supportedByProduct: true,
                            interfaceId: iface.sId,
                            workshopProductId: si.productId.sId
                        });
                    }
                });

                workshopProductImportSelections.push({
                    workshopIdTo: workshopId,
                    workshopProductIdFrom: si.productId.sId,
                    combinations: workshopProductCombinations,
                    interfaces: workshopProductInterfaces
                });
            }
        });

        // import the selected products, test combinations, and interfaces into the current workshop
        importWorkshopProductSettings(workshopProductImportSelections, new AbortController()).then((importResponse) => {
            if (importResponse.failed) {
                importResponse.errorText && showUserFeedback(importResponse.errorText);
                return;
            }
            setOpenState(false);
        }).catch((error) => {
            showUserFeedback(String(error));
        });
    };

    return <NonRouterModal title={``} openState={openState} setOpenState={setOpenState}
        description={[
            <Typography variant='body2'>{'Import instances from previous workshops into the current workshop'}</Typography>
        ]}
        cancel
        buttons={[<Button onClick={importProductsToWorkshop} startIcon={<ImportExportIcon />}> {'Import'} </Button >]}>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='bold'>{'Workshop Name'}</Typography>
            <Autocomplete id='WorkshopSelMultiImport'
                options={workshops} value={selWorkshop}
                sx={{ width: 300, marginBottom: 20, marginTop: 20 }}
                renderInput={(params) => <TextField {...params} label="Select Workshop" />}
                getOptionLabel={workshop => workshop.name}
                onChange={(event, value) => setSelWorkshop(value)}
            />
            <Box overflow='auto' maxHeight='550px' >
                {workshopProds.length === 0 ? <Typography variant='body2' color='text.secondary'>{'No products found for the selected workshop.'}</Typography>
                    : workshopProds.map((wp, index) => (
                        <ProductSelectionAccordion expanded={expanded} exName={`panel${index}`}
                            combinations={supportedCombos}
                            productInfo={wp} selectionInfo={selectionInfos.find(si => si.productId.sId === wp.wsProduct.id)}
                            handleChange={handleAccordionChange} handleSelectionChange={handleSelectionChange} />
                    ))
                }
            </Box>
        </div>
    </NonRouterModal>
}

const ProductSelectionAccordion = ({ expanded, exName, combinations, productInfo, selectionInfo, handleChange, handleSelectionChange }:
    {
        expanded: string,
        exName: string,
        combinations: TestCombinationInterfaces[],
        productInfo: WorkshopProductWIthInterface,
        selectionInfo: SelectedWorkshopInteractions | undefined,
        handleChange: (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => void,
        handleSelectionChange: (productId: number, consumerId: number, providerId: number, iface: number, selected: boolean) => void
    }) => {

    if (!selectionInfo) {
        return null;
    }
    const handleChangeProdSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleSelectionChange(productInfo.wsProduct.id, 0, 0, 0, event.target.checked);
    };

    return <ExpandControlledAccordion expanded={expanded === exName} exName={exName} handleChange={handleChange}>
        <SwitchedAccordionSummary acName={`${productInfo.product.name} (${productInfo.wsProduct.productVersion ?? ''})`}
            checked={selectionInfo?.productId.sSelected ?? false} handleswitchChange={handleChangeProdSwitch} />
        {combinations.map((combo, index) => (
            <InteractionAccordionDetails
                key={index}
                combination={combo}
                productInfo={productInfo}
                selectionInfo={selectionInfo}
                handleSelectionChange={handleSelectionChange}
            />
        ))}
    </ExpandControlledAccordion>
}

const InteractionAccordionDetails = ({ combination, productInfo, selectionInfo, handleSelectionChange }:
    {
        combination: TestCombinationInterfaces,
        productInfo: WorkshopProductWIthInterface,
        selectionInfo: SelectedWorkshopInteractions | undefined,
        handleSelectionChange: (productId: number, consumerId: number, providerId: number, iface: number, selected: boolean) => void
    }) => {
    if (!selectionInfo) return null;
    if (selectionInfo.disabledTestCombinationIds.find(tc => tc === combination.testCombination.id) === undefined
        && selectionInfo.testCombinationConsumerIds.find(tc => tc.sId === combination.testCombination.id) === undefined) {
        return null;    // do not show if not available
    }
    const combinationDisabled = selectionInfo.disabledTestCombinationIds.find(tc => tc === combination.testCombination.id) ? true : false;
    const providerSelected = selectionInfo.testCombinationProviderIds.find(tc => tc.sId === combination.testCombination.id)?.sSelected ?? false;
    const consumerSelected = selectionInfo.testCombinationConsumerIds.find(tc => tc.sId === combination.testCombination.id)?.sSelected ?? false;

    const providerOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (combination.testCombination.id === undefined) return;
        handleSelectionChange(productInfo.wsProduct.id, 0, combination.testCombination.id, 0, e.target.checked);
    };

    const consumerOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (combination.testCombination.id === undefined) return;
        handleSelectionChange(productInfo.wsProduct.id, combination.testCombination.id, 0, 0, e.target.checked);
    };

    return <AccordionDetails>
        <Grid container size={12}>
            <Grid size={4} mt={7} >
                <Typography variant='subtitle1' >{combination.testCombination.combinationName ?? ''}</Typography>
            </Grid>
            <Grid size={2} >
                <FormControlLabel
                    control={<Checkbox checked={providerSelected} disabled={combinationDisabled} onChange={providerOnChangeHandler}
                        inputProps={{ 'aria-label': 'controlled' }} />} label={combination.testCombination.combinationProvider ?? ''} />
            </Grid>
            <Grid size={2} >
                <FormControlLabel
                    control={<Checkbox checked={consumerSelected} disabled={combinationDisabled} onChange={consumerOnChangeHandler}
                        inputProps={{ 'aria-label': 'controlled' }} />} label={combination.testCombination.combinationConsumer ?? ''} />
            </Grid>
            <Grid size={4}>
                {combination.interfaces.map(iface => (
                    <InterfaceAccordionDetails iface={iface} productInfo={productInfo} selectionInfo={selectionInfo} handleSelectionChange={handleSelectionChange} />
                ))}
            </Grid>
        </Grid>
    </AccordionDetails>
}

const InterfaceAccordionDetails = ({ iface, productInfo, selectionInfo, handleSelectionChange }:
    {
        iface: Interface,
        productInfo: WorkshopProductWIthInterface,
        selectionInfo: SelectedWorkshopInteractions,
        handleSelectionChange: (productId: number, consumerId: number, providerId: number, iface: number, selected: boolean) => void
    }) => {
    if (selectionInfo.disabledInterfaceIds.find(i => i === iface.id) === undefined
        && selectionInfo.interfaceIds.find(i => i.sId === iface.id) === undefined) {
        return null;    // do not show if not available
    }

    const ifaceDisabled = selectionInfo.disabledInterfaceIds.find(i => i === iface.id) ? true : false;
    const ifaceSelected = selectionInfo.interfaceIds.find(i => i.sId === iface.id)?.sSelected ? true : false;

    const handleChangeProdSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
         handleSelectionChange(productInfo.wsProduct.id, 0, 0, iface.id, event.target.checked);
    };

    return <FormControlLabel key={iface.name} label={iface.name}
        control={<BlueSwitch disabled={ifaceDisabled} checked={ifaceSelected} onChange={handleChangeProdSwitch} />} />
}